import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import type { CompanyComparison, HotZonesView, PersonalAudit } from '@/api/types';
import { HotZoneItem } from '@/components/HotZoneItem';
import { COMPANY_NAMES } from '@/components/CompanyLogo';
import { ErrorView } from '@/components/ErrorView';
import { FullPageSpinner } from '@/components/Spinner';

export function AnalyticsPage() {
  const [hot, setHot] = useState<HotZonesView | null>(null);
  const [comparison, setComparison] = useState<CompanyComparison | null>(null);
  const [audit, setAudit] = useState<PersonalAudit | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let off = false;
    // Settled, not all: one failing section must not blank the two that loaded.
    Promise.allSettled([api.getHotZones(), api.getCompanyComparison(), api.getPersonalAudit()])
      .then(([h, c, a]) => {
        if (off) return;
        if (h.status === 'fulfilled') setHot(h.value);
        if (c.status === 'fulfilled') setComparison(c.value);
        if (a.status === 'fulfilled') setAudit(a.value);
        if (h.status === 'rejected' && c.status === 'rejected' && a.status === 'rejected') {
          setErr(h.reason);
        }
        setLoading(false);
      });
    return () => { off = true; };
  }, []);

  if (loading) return <FullPageSpinner />;
  if (err) return <div className="page"><ErrorView error={err} /></div>;

  return (
    <div className="page">
      <h1 className="page-title">Аналитика</h1>

      {/* Hot zones — ТЗ §7.3 A */}
      <section>
        <h2 style={{ fontSize: 18, margin: '0 0 8px' }}>🔥 Где сейчас горячо</h2>
        {hot && hot.zones.length > 0 ? (
          hot.zones.map((z, i) => <HotZoneItem key={i} zone={z} rank={i + 1} />)
        ) : (
          <div className="card" style={{ color: 'var(--bk-hint)' }}>
            {hot?.hint ?? 'Данных пока нет.'}
          </div>
        )}
      </section>

      {/* Company comparison — ТЗ §7.3 Б */}
      {comparison && comparison.ranking.length > 0 && (
        <section>
          <h2 style={{ fontSize: 18, margin: '16px 0 8px' }}>
            📊 Сравнение платформ — {comparison.district}
          </h2>
          <div className="card" style={{ padding: 0 }}>
            {comparison.ranking.map((r, i) => (
              <div
                key={r.company}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: 12,
                  borderTop: i === 0 ? 'none' : '1px solid var(--bk-border)',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{COMPANY_NAMES[r.company]}</div>
                  <div style={{ color: 'var(--bk-hint)', fontSize: 12 }}>{r.sampleSize} смен</div>
                </div>
                <div style={{ fontWeight: 700 }}>{Math.round(r.medianEphRub)} ₽/час</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Personal audit — ТЗ §7.3 В */}
      {audit && (
        <section>
          <h2 style={{ fontSize: 18, margin: '16px 0 8px' }}>📈 Твоя эффективность</h2>
          <div className="card">
            {audit.userEphRub != null && audit.topEphRub != null && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>
                    {Math.round(audit.userEphRub)} ₽
                  </div>
                  <div style={{ color: 'var(--bk-hint)', fontSize: 12 }}>Твоя медиана/час</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--bk-link)' }}>
                    {Math.round(audit.topEphRub)} ₽
                  </div>
                  <div style={{ color: 'var(--bk-hint)', fontSize: 12 }}>ТОП-20%</div>
                </div>
              </div>
            )}
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.4 }}>{audit.message}</p>
          </div>
        </section>
      )}
    </div>
  );
}
